/* ----------------------------------------------------------------------------
   PDP YOUCHEWS MAIN — section script
   Custom element <pdp-main> drives the whole panel client-side:
   - option pills -> variant resolution (matched against embedded product JSON)
   - purchase-type pills -> selling plan on/off (hidden selling_plan input)
   - price / compare / discount badge / order summary / ATC label updates
   - square media carousel with pagination dots
   Add-to-cart itself is handled by the theme's ProductForm (is="product-form"),
   so the cart drawer, error banners and cart events keep working unchanged.
---------------------------------------------------------------------------- */

(() => {
  if (customElements.get('pdp-main')) return;

  class PdpMain extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-pdp-data]');
      if (!dataEl) return;

      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error('[pdp-main] invalid config JSON', error);
        return;
      }

      this.form = document.getElementById(this.dataset.formId);
      this.idInput = this.form ? this.form.querySelector('[data-pdp-input-id]') : null;
      this.planInput = this.form ? this.form.querySelector('[data-pdp-input-plan]') : null;
      this.planSelect = this.querySelector('[data-pdp-plan-select]');

      this.priceEl = this.querySelector('[data-pdp-price]');
      this.compareWrapEl = this.querySelector('[data-pdp-compare-wrap]');
      this.compareEl = this.querySelector('[data-pdp-compare]');
      this.badgeEl = this.querySelector('[data-pdp-badge]');
      this.offerEl = this.querySelector('[data-pdp-summary-offer]');
      this.unitRowEl = this.querySelector('[data-pdp-summary-unit-row]');
      this.unitEl = this.querySelector('[data-pdp-summary-unit]');
      this.todayEl = this.querySelector('[data-pdp-summary-today]');
      this.atcButton = this.querySelector('[data-pdp-atc]');
      this.atcLabelEl = this.querySelector('[data-pdp-atc-label]');

      this.addEventListener('change', this.onChange.bind(this));
      this.initCarousel();
      this.initPromoButtons();
      this.sync({ scrollMedia: false, replaceUrl: false });

      const initialVariant = this.currentVariant();
      if (initialVariant && initialVariant.mediaIndex) {
        this.goToSlide(initialVariant.mediaIndex, false);
      }
    }

    initPromoButtons() {
      this.querySelectorAll('[data-pdp-scroll-to-checkout]').forEach((button) => {
        button.addEventListener('click', () => {
          const target =
            this.querySelector('.pdp-main__block--plan_picker') ||
            this.querySelector('.pdp-main__block--purchase_type_v2') ||
            this.querySelector('.pdp-main__block--variant_picker') ||
            this.querySelector('.pdp-main__block--atc');
          if (!target) return;
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        });
      });
    }

    onChange(event) {
      const target = event.target;
      if (
        target.matches('[data-pdp-option]') ||
        target.matches('[data-pdp-plan-type]') ||
        target.matches('[data-pdp-plan-select]')
      ) {
        this.sync({ scrollMedia: true, replaceUrl: true });
      }
    }

    /* ------------------------------------------------------------- state */

    currentVariant() {
      const variants = this.config.variants || [];
      const checked = [...this.querySelectorAll('input[data-pdp-option]:checked')];

      if (!checked.length) {
        if (this.idInput) {
          const fromInput = variants.find((v) => String(v.id) === String(this.idInput.value));
          if (fromInput) return fromInput;
        }
        return variants[0] || null;
      }

      const selection = checked.map((input) => [Number(input.dataset.pdpOption), input.value]);
      return (
        variants.find((variant) =>
          selection.every(([position, value]) => variant.options[position - 1] === value)
        ) || null
      );
    }

    purchaseType() {
      const input = this.querySelector('input[data-pdp-plan-type]:checked');
      if (input) return input.value;
      return this.config.requiresSellingPlan && this.hasPlans() ? 'subscription' : 'one_time';
    }

    hasPlans() {
      return Array.isArray(this.config.sellingPlans) && this.config.sellingPlans.length > 0;
    }

    selectedPlan() {
      if (!this.hasPlans()) return null;
      if (this.planSelect) {
        const found = this.config.sellingPlans.find(
          (plan) => String(plan.id) === String(this.planSelect.value)
        );
        if (found) return found;
      }
      return this.config.sellingPlans[0];
    }

    unitCount(variant) {
      if (!variant) return null;
      if (variant.count) return variant.count;
      const matches = String(variant.title).replace(/,/g, '').match(/\d+/g);
      if (!matches) return null;
      return Math.max(...matches.map(Number)) || null;
    }

    /* ------------------------------------------------------------- money */

    formatMoney(cents) {
      if (cents == null) return '';
      const format = this.config.moneyFormat || '${{amount}}';
      const value = cents / 100;

      const withDelimiters = (number, precision, thousands, decimal) => {
        const fixed = number.toFixed(precision);
        const [whole, fraction] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? grouped + decimal + fraction : grouped;
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
        switch (placeholder) {
          case 'amount':
            return withDelimiters(value, 2, ',', '.');
          case 'amount_no_decimals':
            return withDelimiters(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return withDelimiters(value, 2, "'", '.');
          default:
            return match;
        }
      });
    }

    applyTemplate(template, variant, plan, count) {
      if (!template) return '';
      return template
        .replace(/\[count\]/g, count != null ? String(count) : '')
        .replace(/\[variant\]/g, variant ? variant.title : '')
        .replace(/\[frequency\]/g, plan && plan.frequency ? plan.frequency : '')
        .replace(/\s{2,}/g, ' ')
        .trim();
    }

    /* -------------------------------------------------------------- sync */

    sync({ scrollMedia = false, replaceUrl = false } = {}) {
      const templates = this.config.templates || {};
      const variant = this.currentVariant();
      const type = this.purchaseType();
      const plan = type === 'subscription' ? this.selectedPlan() : null;
      const allocation =
        plan && variant
          ? (variant.allocations || []).find((a) => String(a.planId) === String(plan.id))
          : null;

      const price = allocation ? allocation.price : variant ? variant.price : null;
      let compare = null;
      if (variant) {
        compare = allocation
          ? allocation.compareAtPrice != null
            ? allocation.compareAtPrice
            : variant.price
          : variant.compareAtPrice;
      }
      const hasCompare = compare != null && price != null && compare > price;
      const count = this.unitCount(variant);

      if (this.priceEl && price != null) this.priceEl.textContent = this.formatMoney(price);

      if (this.compareWrapEl) this.compareWrapEl.hidden = !hasCompare;
      if (this.compareEl && hasCompare) this.compareEl.textContent = this.formatMoney(compare);
      if (this.badgeEl && hasCompare) {
        let percent = plan && plan.percent ? plan.percent : null;
        if (percent == null) percent = Math.round(100 - (price * 100) / compare);
        this.badgeEl.textContent = this.applyTemplate(
          (templates.badge || '[percent]% OFF').replace(/\[percent\]/g, String(percent)),
          variant,
          plan,
          count
        );
      }

      if (this.offerEl) {
        const offerTemplate =
          type === 'subscription' ? templates.offerSubscription : templates.offerOneTime;
        this.offerEl.textContent = this.applyTemplate(offerTemplate, variant, plan, count);
      }

      if (this.unitRowEl) {
        const showUnit = count != null && price != null && count > 0;
        this.unitRowEl.hidden = !showUnit;
        if (showUnit && this.unitEl) {
          this.unitEl.textContent = this.formatMoney(Math.floor(price / count));
        }
      }

      if (this.todayEl && price != null) this.todayEl.textContent = this.formatMoney(price);

      this.syncCards(variant, count);

      if (this.atcButton && this.atcLabelEl) {
        let label;
        let disabled = false;
        if (!variant) {
          label = templates.atcUnavailable || 'Unavailable';
          disabled = true;
        } else if (!variant.available) {
          label = templates.atcSoldOut || 'Sold out';
          disabled = true;
        } else {
          label =
            type === 'subscription'
              ? templates.atcSubscription || 'Add subscription to cart'
              : templates.atcOneTime || 'Add to cart';
        }
        this.atcLabelEl.textContent = label;
        this.atcButton.disabled = disabled;
      }

      if (this.idInput && variant) this.idInput.value = variant.id;
      if (this.planInput) {
        /* Empty value = one-time purchase. The dynamic checkout buttons read
           the input's value regardless of its disabled state, so disabling is
           not enough — mirror Shopify's own subscription widgets instead. */
        this.planInput.disabled = false;
        this.planInput.value = type === 'subscription' && plan ? plan.id : '';
      }
      if (this.planSelect) this.planSelect.hidden = type !== 'subscription';

      if (scrollMedia && variant && variant.mediaIndex != null) {
        this.goToSlide(variant.mediaIndex, true);
      }

      if (replaceUrl && variant && this.hasAttribute('data-update-url')) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (error) {
          /* no-op: URL update is cosmetic */
        }
      }

      if (this.form && variant) {
        /* The pickers live outside the form, so Shopify's dynamic checkout
           buttons never see a native change event when we silently update the
           hidden id/selling_plan inputs — without this nudge the wallet keeps
           checking out the state from page load. */
        this.form.dispatchEvent(new Event('change', { bubbles: true }));
        this.form.dispatchEvent(
          new CustomEvent('variant:change', {
            bubbles: true,
            detail: {
              formId: this.form.id,
              variant,
              previousVariant: this._previousGalleryVariant || variant,
              sellingPlanId: plan ? plan.id : null,
            },
          })
        );
        this._previousGalleryVariant = variant;
      }
    }

    /* Purchase type V2: per-card price / compare / subtitle / banner.
       Cards always show the subscription price on the subscription card and
       the one-time price on the one-time card, independent of which purchase
       type is currently selected. */
    syncCards(variant, count) {
      const cards = this.querySelectorAll('[data-pdp-card]');
      if (!cards.length) return;

      const plan = this.hasPlans() ? this.selectedPlan() : null;

      cards.forEach((card) => {
        const isSubscription = card.dataset.pdpCard === 'subscription';
        let price = variant ? variant.price : null;
        let compare = variant ? variant.compareAtPrice : null;

        if (isSubscription && plan && variant) {
          const allocation = (variant.allocations || []).find(
            (a) => String(a.planId) === String(plan.id)
          );
          if (allocation) {
            price = allocation.price;
            compare = allocation.compareAtPrice != null ? allocation.compareAtPrice : variant.price;
          }
        }

        const hasCompare = compare != null && price != null && compare > price;

        const priceEl = card.querySelector('[data-pdp-card-price]');
        if (priceEl && price != null) priceEl.textContent = this.formatMoney(price);

        const compareEl = card.querySelector('[data-pdp-card-compare]');
        if (compareEl) {
          compareEl.hidden = !hasCompare;
          if (hasCompare) compareEl.textContent = this.formatMoney(compare);
        }

        const subEl = card.querySelector('[data-pdp-card-sub]');
        if (subEl && subEl.dataset.template) {
          subEl.textContent = this.applyTemplate(subEl.dataset.template, variant, plan, count);
        }

        const bannerEl = card.querySelector('[data-pdp-card-banner-strong]');
        if (bannerEl && bannerEl.dataset.template) {
          let percent = plan && plan.percent ? plan.percent : null;
          if (percent == null && variant && price != null && variant.price > price) {
            percent = Math.round(100 - (price * 100) / variant.price);
          }
          if (percent != null) {
            bannerEl.textContent = bannerEl.dataset.template.replace(/\[percent\]/g, String(percent));
          }
        }
      });
    }

    /* ---------------------------------------------------------- carousel */

    initCarousel() {
      this.slider = this.querySelector('[data-pdp-carousel]');
      this.dots = [...this.querySelectorAll('[data-pdp-dot]')];
      if (!this.slider) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goToSlide(index, true));
      });

      this.slider.addEventListener(
        'scroll',
        () => {
          clearTimeout(this._scrollTimer);
          this._scrollTimer = setTimeout(() => this.onSlideSettled(), 90);
        },
        { passive: true }
      );

      this.updateDots(0);
    }

    activeIndex() {
      if (!this.slider || !this.slider.clientWidth) return 0;
      return Math.round(this.slider.scrollLeft / this.slider.clientWidth);
    }

    goToSlide(index, smooth) {
      if (!this.slider) return;
      const slide = this.slider.children[index];
      if (!slide) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slider.scrollTo({
        left: slide.offsetLeft,
        behavior: smooth && !reduceMotion ? 'smooth' : 'auto',
      });
    }

    onSlideSettled() {
      const index = this.activeIndex();
      this.updateDots(index);
      [...this.slider.children].forEach((slide, slideIndex) => {
        if (slideIndex !== index) {
          slide.querySelectorAll('video').forEach((video) => video.pause());
        }
      });
    }

    updateDots(index) {
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }

  customElements.define('pdp-main', PdpMain);
})();
